import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc.js";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const resolveOutletInstant = (
  day: string,
  time: string,
  timezone: string,
): dayjs.Dayjs => {
  const wallClock = `${day}T${time}`;

  if (!timezone) return dayjs(wallClock);

  try {
    return dayjs.tz(wallClock, timezone);
  } catch {
    return dayjs(wallClock);
  }
};

/**
 * Checks if the minimum lead time requirement is met
 * @param minimumLeadTimeInMinutes - The minimum lead time required in minutes
 * @param scheduledTime - Optional scheduled time object with day and time
 * @param timezone - IANA zone of the outlet the time belongs to
 * @returns true when the target is closer than the minimum lead time (warning needed).
 * With no scheduled time the target is "now", so any positive minimum returns true.
 */
export const isUnderMinimumLeadTime = (
  minimumLeadTimeInMinutes: number,
  scheduledTime?: { day: string; time: string },
  timezone: string = "",
): boolean => {
  const now = dayjs();

  const targetTime = scheduledTime
    ? resolveOutletInstant(scheduledTime.day, scheduledTime.time, timezone)
    : now;

  if (!targetTime.isValid()) throw new Error("Invalid scheduled time");

  const differenceInMinutes = targetTime.diff(now, "minute");

  return differenceInMinutes < minimumLeadTimeInMinutes;
};

/**
 * Derives the ISO 8601 "available at" datetime from the basket's scheduled time.
 * This is the fulfillment time sent to the menu endpoints so availability is
 * evaluated against the customer's selected slot.
 * @param scheduledTime - Optional scheduled time object with day and time
 * @param timezone - IANA zone of the outlet the slot belongs to
 * @returns ISO 8601 datetime string, or undefined when there is no (valid)
 * scheduled time — menus are then evaluated against "now" (unchanged behavior).
 */
export const getAvailableAt = (
  scheduledTime?: {
    day: string;
    time: string;
  },
  timezone: string = "",
): string | undefined => {
  if (!scheduledTime) return undefined;

  const availableAt = resolveOutletInstant(
    scheduledTime.day,
    `${scheduledTime.time}:00`,
    timezone,
  );

  return availableAt.isValid() ? availableAt.toISOString() : undefined;
};
